import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Company, CompanyStatus } from '../../../../types/company';
import { CustomerQueries, GetCustomersResponse, GetCustomersVariables } from '../../../../graphql/customer.graphql';
import LoadingPackage from '../../Common/LoadingPackage';
import KanbanCompanyItem from './KanbanCompanyItem';
import { translate } from '../../../../types/translation/Translator';
import { SearchFilter } from '../../../components/Filter';
import { CompanyFilter } from '../../../../VentoryUI/components/filters/CompanyFilter/CompanyFilter';
import { FilterStorageKey } from '../../../../VentoryUI/components/filters/common/filter.util';
import { useRequest } from '../../../../requests/common/requestHook';
import { CompanyRequest } from '../../../../requests/company.request';

export const KANBAN_STATUSES: CompanyStatus[] = [
  CompanyStatus.trial,
  CompanyStatus.active,
  CompanyStatus.partner,
  CompanyStatus.expired,
  CompanyStatus.archived,
  CompanyStatus.demo,
];

export default function AdministratorCustomerPane() {
  const [customers, setCustomers] = useState<Map<string, Company>>(new Map());
  const allItems = useMemo(() => Array.from(customers.values()), [customers]);
  const [items, setItems] = useState<Company[]>(allItems);

  const [draggedCompany, setDraggedCompany] = useState<Company | null>(null);
  const [dropTargetStatus, setDropTargetStatus] = useState<CompanyStatus | null>(null);
  const [loadingCompanyIds, setLoadingCompanyIds] = useState<Set<string>>(new Set());

  const { loading } = useQuery<GetCustomersResponse, GetCustomersVariables>(CustomerQueries.get, {
    onCompleted: res => {
      const customerMap = new Map<string, Company>();
      res.customers.data.forEach(c => {
        const company = new Company(c);
        customerMap.set(company.id, company);
      });
      setCustomers(customerMap);
    },
  });

  const [updateCompanyStatus, { loading: updateStatusLoading }] = useRequest(CompanyRequest.updateStatus);

  const updateCompanyStatusMock = async (company: Company, status: CompanyStatus) => {
    if (company.status === status) return;
    setLoadingCompanyIds(lci => new Set(lci).add(company.id));

    try {
      const updatedCompany = await updateCompanyStatus({ companyId: company.id, status });
      if (updatedCompany) setCustomers(new Map(customers).set(updatedCompany.id, updatedCompany));
    } catch (e) {
    } finally {
      loadingCompanyIds.delete(company.id);
      setLoadingCompanyIds(new Set(loadingCompanyIds));
    }
  };

  const filter = new CompanyFilter(FilterStorageKey.COMPANY_CUSTOMER);

  const companyStatusFilterProps = filter.getFilterProps().companyStatus;
  const filteredStatuses = companyStatusFilterProps?.size
    ? KANBAN_STATUSES.filter(status => companyStatusFilterProps.has(status))
    : KANBAN_STATUSES;

  return (
    <div className='flex h-full flex-col gap-2 overflow-hidden'>
      <div className='content-x'>
        <SearchFilter filter={filter} placeholder='Filter Customers' items={allItems} setItems={setItems} />
      </div>
      {loading ? (
        <div className='flex flex-grow items-center justify-center'>
          <LoadingPackage />
        </div>
      ) : (
        <div className='content-x content-b-screen flex-grow overflow-x-auto overflow-y-hidden py-2'>
          <div className='flex h-full gap-4'>
            {filteredStatuses.map(status => {
              const customers = items
                .filter(customer => customer.status === status)
                .sort((a, b) => a.name.localeCompare(b.name));

              return (
                <div
                  key={status}
                  className={`flex h-full w-[200px] min-w-[200px] flex-col gap-4 rounded-2 py-3 transition-all ${
                    dropTargetStatus === status
                      ? 'bg-blue-50 ring-2 ring-blue-300 ring-offset-2 ring-offset-white'
                      : 'bg-gray-100'
                  }`}
                  onDragOver={event => {
                    event.preventDefault();
                    if (draggedCompany && dropTargetStatus !== status) setDropTargetStatus(status);
                  }}
                  onDragEnter={event => {
                    event.preventDefault();
                    if (draggedCompany) setDropTargetStatus(status);
                  }}
                  onDragLeave={event => {
                    const next = event.relatedTarget as Node | null;
                    if (!event.currentTarget.contains(next)) {
                      setDropTargetStatus(current => (current === status ? null : current));
                    }
                  }}
                  onDrop={() => {
                    if (!draggedCompany) return;

                    updateCompanyStatusMock(draggedCompany, status);
                    setDraggedCompany(null);
                    setDropTargetStatus(null);
                  }}
                >
                  <div className='flex items-center justify-between px-3'>
                    <p className='font-semibold text-title'>{translate({ key: status })}</p>
                    <p className='flex items-center justify-center rounded-full bg-white px-1.5 py-0.5 text-xs font-medium text-gray-600'>
                      {customers.length}
                    </p>
                  </div>

                  <div
                    className='flex w-[200px] flex-grow flex-col items-center gap-2 overflow-y-auto overflow-x-hidden px-3'
                    style={{ scrollbarWidth: 'thin' }}
                  >
                    {customers.map(company => (
                      <KanbanCompanyItem
                        loading={loadingCompanyIds.has(company.id)}
                        key={company.id}
                        company={company}
                        onStatusChange={updateCompanyStatusMock}
                        onDragStart={setDraggedCompany}
                        onDragEnd={() => {
                          setDraggedCompany(null);
                          setDropTargetStatus(null);
                        }}
                      />
                    ))}

                    {!customers.length && (
                      <div className='rounded-xl border border-dashed border-gray-300 p-4 text-sm text-gray-500'>
                        Drop customers here
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
